// =============================================================================
// MeuDriver — Hook de disponibilidade do instrutor (H1)
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type {
  InstructorAvailabilityRow,
  InstructorAvailabilityInsert,
  InstructorBlockedSlotRow,
  InstructorBlockedSlotInsert,
  AvailableSlot,
} from '@/types/database'

// ─── Chaves de query ──────────────────────────────────────────────────────────

const KEYS = {
  weekly: (id: string) => ['instructor_availability', id] as const,
  slots: (id: string, date: string) => ['available_slots', id, date] as const,
  blocked: (id: string) => ['instructor_blocked_slots', id] as const,
}

// ─── useInstructorAvailability ────────────────────────────────────────────────

/**
 * Busca todos os slots semanais de disponibilidade de um instrutor.
 * Leitura pública — usado na tela de agendamento (aluno) e no editor (instrutor).
 */
export function useInstructorAvailability(instructorId: string | undefined) {
  return useQuery({
    queryKey: KEYS.weekly(instructorId ?? ''),
    enabled: Boolean(instructorId),
    queryFn: async (): Promise<InstructorAvailabilityRow[]> => {
      const { data, error } = await supabase
        .from('instructor_availability')
        .select('*')
        .eq('instructor_id', instructorId!)
        .order('day_of_week')
        .order('start_time')

      if (error) throw new Error(error.message)
      return (data ?? []) as InstructorAvailabilityRow[]
    },
  })
}

// ─── useAvailableSlots ────────────────────────────────────────────────────────

/**
 * Chama a RPC get_available_slots para uma data específica.
 * Retorna todos os slots do instrutor naquele dia com flag is_available.
 */
export function useAvailableSlots(
  instructorId: string | undefined,
  date: string | undefined, // "YYYY-MM-DD"
) {
  return useQuery({
    queryKey: KEYS.slots(instructorId ?? '', date ?? ''),
    enabled: Boolean(instructorId && date),
    queryFn: async (): Promise<AvailableSlot[]> => {
      const { data, error } = await supabase.rpc('get_available_slots', {
        p_instructor_id: instructorId!,
        p_date: date!,
      })

      if (error) throw new Error(error.message)
      return (data ?? []) as AvailableSlot[]
    },
    staleTime: 30_000, // 30s — revalida frequentemente para refletir novos bookings
  })
}

// ─── useBlockedSlots ─────────────────────────────────────────────────────────

/**
 * Busca bloqueios pontuais de um instrutor (próximos 90 dias).
 */
export function useBlockedSlots(instructorId: string | undefined) {
  return useQuery({
    queryKey: KEYS.blocked(instructorId ?? ''),
    enabled: Boolean(instructorId),
    queryFn: async (): Promise<InstructorBlockedSlotRow[]> => {
      const today = new Date().toISOString().slice(0, 10)
      const future = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10)

      const { data, error } = await supabase
        .from('instructor_blocked_slots')
        .select('*')
        .eq('instructor_id', instructorId!)
        .gte('blocked_date', today)
        .lte('blocked_date', future)
        .order('blocked_date')
        .order('start_time', { nullsFirst: true })

      if (error) throw new Error(error.message)
      return (data ?? []) as InstructorBlockedSlotRow[]
    },
  })
}

// ─── useManageAvailability ────────────────────────────────────────────────────

/**
 * Mutations para o instrutor gerenciar seus slots e bloqueios.
 */
export function useManageAvailability() {
  const { user } = useAuth()
  const qc = useQueryClient()

  // ── Upsert slot semanal ───────────────────────────────────────────────────

  const upsertSlot = useMutation({
    mutationFn: async (payload: InstructorAvailabilityInsert) => {
      const { error } = await supabase
        .from('instructor_availability')
        .upsert(payload, { onConflict: 'instructor_id,day_of_week,start_time' })

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      if (user?.id) qc.invalidateQueries({ queryKey: KEYS.weekly(user.id) })
    },
    onError: (err: Error) => {
      toast.error(`Erro ao salvar horário: ${err.message}`)
    },
  })

  // ── Desativar/remover slot ────────────────────────────────────────────────

  const removeSlot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('instructor_availability')
        .update({ active: false })
        .eq('id', id)

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      if (user?.id) qc.invalidateQueries({ queryKey: KEYS.weekly(user.id) })
    },
    onError: (err: Error) => {
      toast.error(`Erro ao remover horário: ${err.message}`)
    },
  })

  // ── Adicionar bloqueio pontual ────────────────────────────────────────────

  const addBlock = useMutation({
    mutationFn: async (payload: InstructorBlockedSlotInsert) => {
      const { error } = await supabase
        .from('instructor_blocked_slots')
        .upsert(payload, { onConflict: 'instructor_id,blocked_date,start_time' })

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      if (user?.id) {
        qc.invalidateQueries({ queryKey: KEYS.blocked(user.id) })
        toast.success('Data bloqueada com sucesso.')
      }
    },
    onError: (err: Error) => {
      toast.error(`Erro ao bloquear data: ${err.message}`)
    },
  })

  // ── Remover bloqueio pontual ──────────────────────────────────────────────

  const removeBlock = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('instructor_blocked_slots')
        .delete()
        .eq('id', id)

      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      if (user?.id) {
        qc.invalidateQueries({ queryKey: KEYS.blocked(user.id) })
        toast.success('Bloqueio removido.')
      }
    },
    onError: (err: Error) => {
      toast.error(`Erro ao remover bloqueio: ${err.message}`)
    },
  })

  return { upsertSlot, removeSlot, addBlock, removeBlock }
}
